/**
 * Kitchen — settings, restrictions and the second eater's link
 * (#1323, #1295, #1325, #1285).
 *
 * ⚠️ A RESTRICTION IS ENTERED THROUGH A DIFFERENT CONTROL FROM A TASTE, on
 * purpose and visibly. Nobody should discover their allergy was filed as a
 * dislike, and the surest way to cause that is a single screen where "no" and
 * "allergic" sit next to each other looking like two strengths of the same
 * thing.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  twoPlates,
  SEVERITY_COPY,
  VARIETY_COPY,
  LEFTOVER_LABELS,
  detectedTimezone,
  type Eater,
  type Household,
} from "../api/twoPlates";
import { signOut, toSignIn, ApiError, api } from "../lib/api";
// ⛔ The cache is per ORIGIN, not per user. A second person signing in on the
// same phone must not read the first one's kitchen.
import { clearOfflineData } from "../lib/install";
import { Upgrade, UpgradeNote } from "../components/Upgrade";
import { Reminders } from "../components/Reminders";
import { Members } from "../components/Members";
import { HouseholdSettings } from "../components/HouseholdSettings";
import { useQuery as useCapQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

/**
 * ⚠️ A link, never a nag. It does not check eligibility here on purpose — the
 * review screen explains the rule better than a hidden link would, and a
 * disappearing menu item is how people conclude a feature is broken.
 */
/**
 * ⚠️ Asks the server what this person may do rather than assuming the account
 * holder is the only one looking. A member reaching the Kitchen sees the
 * settings and cannot change them, which is more use than an empty page.
 */
function SettingsPanel() {
  const q = useCapQuery<{ you: { can: string[] } }>({
    queryKey: ["members"],
    queryFn: () => api("/members"),
  });
  return <HouseholdSettings canEdit={!!q.data?.you.can.includes("settings")} />;
}

function ReviewPrompt() {
  return (
    <section className="border-line rounded-xl border p-5">
      <h2 className="font-semibold">Tell people how it went</h2>
      <p className="text-muted mt-1 text-sm">
        If Two Plates changed how your house eats, say so where somebody deciding
        can read it.
      </p>
      <Link
        to="/review"
        className="border-line mt-3 inline-block rounded-lg border px-4 py-2 text-sm font-medium"
      >
        Write a review
      </Link>
    </section>
  );
}
import { SetUpNotifications } from "../components/SetUpNotifications";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function Kitchen() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["household"], queryFn: twoPlates.household });

  if (isLoading) return <p className="text-muted text-sm">Loading…</p>;
  const household = data?.household;
  if (!household) {
    return (
      <div>
        <p className="text-muted text-sm">No household yet.</p>
        <button
          onClick={() => navigate("/setup")}
          className="bg-plate-b text-ground tap mt-4 w-full rounded-lg font-semibold"
        >
          Set one up
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{household.name}</h1>
        <p className="text-muted mt-1 text-sm">
          {household.eaters.map((e) => e.name).join(" and ")}
        </p>
      </header>

      <Upgrade />

      <Preferences household={household} />

      {household.eaters.map((eater, i) => (
        <EaterCard key={eater.id} eater={eater} index={i} />
      ))}

      <Reminders />

      <SetUpNotifications />

      <SettingsPanel />

      <Members />

      <ReviewPrompt />

      <History household={household} />

      {/* ⛔ /health is listed separately and not folded under Privacy. Washington's
          My Health My Data Act requires the consumer health data policy to be its
          own distinctly linked document. */}
      <p className="text-muted text-center text-xs">
        <a href="/terms" className="underline underline-offset-4">Terms</a>
        {" · "}
        <a href="/privacy" className="underline underline-offset-4">Privacy</a>
        {" · "}
        <a href="/health" className="underline underline-offset-4">Health data</a>
        {" · "}
        <a href="/disclaimer" className="underline underline-offset-4">Food safety</a>
      </p>

      <button
        onClick={() => { clearOfflineData(); signOut(); toSignIn("Signed out. Your plan is still here when you come back."); }}
        className="text-muted self-start text-xs underline underline-offset-4"
      >
        Sign out
      </button>
    </div>
  );
}

function Preferences({ household }: { household: Household }) {
  const queryClient = useQueryClient();
  const { data: billing } = useQuery({ queryKey: ["billing"], queryFn: twoPlates.billing });
  const save = useMutation({
    mutationFn: twoPlates.updateHousehold,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["household"] }),
  });

  return (
    <section className="flex flex-col gap-5">
      <h2 className="text-muted text-xs uppercase tracking-wide">How we shop and cook</h2>

      <label className="flex flex-col gap-2">
        <span className="text-sm">Shopping day</span>
        <select
          value={household.shopDay}
          onChange={(e) => save.mutate({ shopDay: Number(e.target.value) })}
          className="bg-surface border-line tap rounded-lg border px-4 text-base"
        >
          {DAYS.map((d, i) => (
            <option key={d} value={i}>{d}</option>
          ))}
        </select>
      </label>

      <Timezone household={household} save={save} />

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm">How many days a plan covers</legend>
        <div className="mt-1 flex gap-2">
          {[3, 4, 5, 6, 7].map((n) => {
            const chosen = household.planDays === n;
            const locked = billing?.tier === "free" && n > billing.maxPlanDays;
            return (
              <button
                key={n}
                onClick={() => save.mutate({ planDays: n })}
                aria-pressed={chosen}
                title={locked ? "Plus" : undefined}
                className="border-line tap flex-1 rounded-lg border text-base font-semibold"
                style={
                  chosen
                    ? { borderColor: "var(--color-plate-b)", background: "color-mix(in srgb, var(--color-plate-b) 12%, transparent)" }
                    : undefined
                }
              >
                {n}
                {locked && <span className="text-muted ml-0.5 align-super text-[9px]">+</span>}
              </button>
            );
          })}
        </div>
        <p className="text-muted text-xs">
          {/* Say what it costs rather than quietly pretending day seven's salad
              is as good as day one's. */}
          {household.planDays <= 4
            ? "Three days is roughly how long fresh food lasts, which is why plans are short."
            : "Longer than fresh produce comfortably lasts. The plan will tell you to eat the salad and berries early and the potatoes and cabbage late."}
        </p>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm">Packets or from scratch</legend>
        <p className="text-muted mb-1 text-xs">
          A seasoning packet removes a technique step. A medical restriction overrides
          this on its own, so choosing packets never costs anybody their safety.
        </p>
        <div className="flex gap-2">
          {(["pack", "scratch"] as const).map((style) => (
            <button
              key={style}
              onClick={() => save.mutate({ prefersStyle: style })}
              aria-pressed={household.prefersStyle === style}
              className="border-line tap flex-1 rounded-lg border text-sm font-medium"
              style={
                household.prefersStyle === style
                  ? { borderColor: "var(--color-plate-b)", background: "color-mix(in srgb, var(--color-plate-b) 12%, transparent)" }
                  : undefined
              }
            >
              {style === "pack" ? "Packets are fine" : "From scratch"}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm">Variety across the {household.planDays} nights</legend>
        {/* A setting, not a rule. Some weeks a household WANTS the overlap —
            one cabbage feeding two dinners is a feature, not a failure. */}
        <div className="mt-1 flex flex-col gap-2">
          {(["repeat", "balanced", "varied"] as const).map((level) => {
            const chosen = household.varietyPreference === level;
            return (
              <button
                key={level}
                onClick={() => save.mutate({ varietyPreference: level })}
                aria-pressed={chosen}
                className="border-line rounded-lg border p-3 text-left"
                style={
                  chosen
                    ? { borderColor: "var(--color-plate-b)", background: "color-mix(in srgb, var(--color-plate-b) 12%, transparent)" }
                    : undefined
                }
              >
                <p className="text-sm font-medium">{VARIETY_COPY[level].name}</p>
                <p className="text-muted mt-0.5 text-xs">{VARIETY_COPY[level].note}</p>
              </button>
            );
          })}
        </div>
        <p className="text-muted mt-1 text-xs">
          This changes the order of the deck. It never removes a meal from it.
        </p>
      </fieldset>

      {save.isError && (
        save.error instanceof ApiError && save.error.needsUpgrade
          ? <UpgradeNote error={save.error} />
          : <p role="alert" className="text-warn text-sm">
              {save.error instanceof ApiError ? save.error.message : "Could not save that"}
            </p>
      )}
    </section>
  );
}

function EaterCard({ eater, index }: { eater: Eater; index: number }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const colour = index % 2 === 0 ? "var(--color-plate-a)" : "var(--color-plate-b)";
  const [adding, setAdding] = useState(false);
  const [link, setLink] = useState<string | null>(null);

  const targets = useQuery({
    queryKey: ["restriction-targets"],
    queryFn: twoPlates.restrictionTargets,
    enabled: adding,
  });

  const add = useMutation({
    mutationFn: (body: { target: string; severity: string }) => twoPlates.addRestriction(eater.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["household"] });
      queryClient.invalidateQueries({ queryKey: ["deck"] });
      setAdding(false);
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => twoPlates.removeRestriction(eater.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["household"] });
      queryClient.invalidateQueries({ queryKey: ["deck"] });
    },
  });

  // Reveals the existing link. ⛔ Does not rotate it — that is `invite` below.
  const showLink = useMutation({
    mutationFn: () => twoPlates.showInvite(eater.id),
    onSuccess: (res) => { if (res.invite) setLink(res.invite.url); },
  });

  const invite = useMutation({
    mutationFn: () => twoPlates.invite(eater.id),
    onSuccess: (res) => {
      setLink(res.invite.url);
      queryClient.invalidateQueries({ queryKey: ["household"] });
    },
  });

  const answered = Object.keys(eater.answers).length;

  return (
    <section className="border-line rounded-xl border p-5">
      <header className="flex items-center gap-2">
        <span aria-hidden className="h-3 w-3 rounded-full" style={{ background: colour }} />
        <h2 className="font-semibold">{eater.name}</h2>
      </header>

      <p className="text-muted mt-2 text-xs">
        {answered} of 16 foods answered · leftovers:{" "}
        {LEFTOVER_LABELS[eater.leftoverTolerance]?.title.toLowerCase() ?? "not said"}
      </p>
      <button
        onClick={() => navigate("/taste")}
        className="text-plate-b mt-1 text-sm underline underline-offset-4"
      >
        {answered === 0 ? "Answer the food cards" : "Change the answers"}
      </button>

      {/* ── restrictions: a different control, deliberately ── */}
      <div className="border-line mt-5 border-t pt-4">
        <h3 className="text-muted text-xs uppercase tracking-wide">Cannot eat</h3>
        <p className="text-muted mt-1 text-xs">
          Not the same as a dislike, and never guessed from one.
        </p>

        {eater.restrictions.length > 0 && (
          <ul className="mt-3 flex flex-col gap-2">
            {eater.restrictions.map((r) => (
              <li
                key={r.id}
                className="border-line flex items-start justify-between gap-3 rounded-lg border p-3"
                style={r.severity === "allergy" ? { borderColor: "var(--color-warn)" } : undefined}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium capitalize">{r.target.replace("_", " ")}</p>
                  <p className="text-muted mt-0.5 text-xs">{SEVERITY_COPY[r.severity]?.note}</p>
                </div>
                <button
                  onClick={() => remove.mutate(r.id)}
                  aria-label={`No longer applies: ${r.target}`}
                  title="Kept as history — it stops applying from today"
                  className="text-muted shrink-0 text-sm underline underline-offset-4"
                >
                  No longer
                </button>
              </li>
            ))}
          </ul>
        )}

        {adding ? (
          <AddRestriction
            targets={targets.data?.targets ?? []}
            loading={targets.isLoading}
            saving={add.isPending}
            error={add.isError ? (add.error as Error).message : null}
            upgrade={add.error instanceof ApiError && add.error.needsUpgrade}
            onCancel={() => setAdding(false)}
            onSave={(target, severity) => add.mutate({ target, severity })}
          />
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="text-plate-b mt-3 text-sm underline underline-offset-4"
          >
            Add something {eater.name} cannot eat
          </button>
        )}
      </div>

      {/* ── the link ── */}
      <div className="border-line mt-5 border-t pt-4">
        <h3 className="text-muted text-xs uppercase tracking-wide">Let them answer</h3>
        {eater.invite ? (
          <p className="text-muted mt-2 text-xs">
            Link sent ·{" "}
            {eater.invite.answeredAt
              ? "answered"
              : eater.invite.openedAt
                ? "opened, not finished"
                : "not opened yet"}
          </p>
        ) : (
          <p className="text-muted mt-2 text-xs">
            Send {eater.name} a link and they answer on their own phone. No account,
            no password — and they see nothing else about the household.
          </p>
        )}

        {link && (
          <div className="border-line mt-3 rounded-lg border p-3">
            <p className="text-muted text-xs">Copy this and send it. It works for 30 days.</p>
            <p className="mt-2 break-all text-xs">{link}</p>
            <button
              onClick={() => navigator.clipboard?.writeText(link)}
              className="text-plate-b mt-2 text-sm underline underline-offset-4"
            >
              Copy
            </button>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-4">
          {/* ⛔ Show comes FIRST and is the safe one. "Send a new link" rotates
              the token and kills whatever the household is already using —
              which is what somebody reaches for when they have simply mislaid
              the URL, and the household then sees an error that looks exactly
              like an expiry (#1345). */}
          {eater.invite && (
            <button
              onClick={() => showLink.mutate()}
              disabled={showLink.isPending}
              className="text-plate-b text-sm underline underline-offset-4 disabled:opacity-50"
            >
              {showLink.isPending ? "Fetching…" : "Show the link again"}
            </button>
          )}
          <button
            onClick={() => invite.mutate()}
            disabled={invite.isPending}
            className="text-muted text-sm underline underline-offset-4 disabled:opacity-50"
          >
            {eater.invite ? "Send a new link" : "Create a link"}
          </button>
        </div>
        {eater.invite && (
          <p className="text-muted mt-1 text-[11px]">
            Showing it again is safe. A NEW link stops the old one working.
          </p>
        )}
      </div>
    </section>
  );
}

function AddRestriction({
  targets,
  loading,
  saving,
  error,
  upgrade,
  onCancel,
  onSave,
}: {
  targets: { key: string; name: string; kind: string; hint: string }[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  upgrade?: boolean;
  onCancel: () => void;
  onSave: (target: string, severity: string) => void;
}) {
  const [target, setTarget] = useState("");
  const [severity, setSeverity] = useState("");

  if (loading) return <p className="text-muted mt-3 text-xs">Loading…</p>;

  return (
    <div className="border-line mt-3 rounded-lg border p-3">
      <label className="flex flex-col gap-2">
        <span className="text-sm">What?</span>
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="bg-surface border-line tap rounded-lg border px-3 text-base"
        >
          <option value="">Choose…</option>
          <optgroup label="Ingredients">
            {targets.filter((t) => t.kind === "ingredient").map((t) => (
              <option key={t.key} value={t.key}>{t.name}</option>
            ))}
          </optgroup>
          <optgroup label="Whole food groups">
            {targets.filter((t) => t.kind === "group").map((t) => (
              <option key={t.key} value={t.key}>{t.name}</option>
            ))}
          </optgroup>
        </select>
      </label>
      {target && (
        <p className="text-muted mt-1 text-xs">
          {targets.find((t) => t.key === target)?.hint}
        </p>
      )}

      <fieldset className="mt-4 flex flex-col gap-2">
        <legend className="text-sm">How serious?</legend>
        {/* Three distinct things, described by what they DO. A slider would
            invite somebody to file an allergy as a strong dislike. */}
        {(["avoid", "medical", "allergy"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setSeverity(s)}
            aria-pressed={severity === s}
            className="border-line rounded-lg border p-3 text-left"
            style={
              severity === s
                ? {
                    borderColor: s === "allergy" ? "var(--color-warn)" : "var(--color-plate-b)",
                    background: "color-mix(in srgb, var(--color-plate-b) 10%, transparent)",
                  }
                : undefined
            }
          >
            <p className="text-sm font-medium">{SEVERITY_COPY[s].name}</p>
            <p className="text-muted mt-0.5 textg-xs text-xs">{SEVERITY_COPY[s].note}</p>
          </button>
        ))}
      </fieldset>

      {error && (
        upgrade
          ? <p role="alert" className="mt-3 text-sm" style={{ color: "var(--color-plate-b)" }}>{error}</p>
          : <p role="alert" className="text-warn mt-3 text-sm">{error}</p>
      )}

      <div className="mt-4 flex gap-3">
        <button onClick={onCancel} className="text-muted flex-1 text-sm underline underline-offset-4">
          Cancel
        </button>
        <button
          disabled={!target || !severity || saving}
          onClick={() => onSave(target, severity)}
          className="bg-plate-b text-ground tap flex-1 rounded-lg text-sm font-semibold disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

const SCORE_WORD: Record<number, string> = { 0: "no", 1: "will eat", 2: "likes", 3: "loves" };

/**
 * What changed, and when (#1308).
 *
 * "Since when does he eat mushrooms" was unanswerable before this. Drift across
 * a household is exactly the thing worth being able to see, and a profile that
 * silently overwrites itself is one nobody can reason about.
 */
function History({ household }: { household: Household }) {
  const rows = household.eaters
    .flatMap((e) => (e.history ?? []).map((h) => ({ ...h, who: e.name })))
    .sort((a, b) => +new Date(b.changedAt) - +new Date(a.changedAt))
    .slice(0, 12);

  if (rows.length === 0) return null;

  return (
    <section>
      <h2 className="text-muted text-xs uppercase tracking-wide">What has changed</h2>
      <ul className="mt-3 flex flex-col gap-2">
        {rows.map((h, i) => (
          <li key={i} className="text-muted text-xs">
            <span className="text-ink">{h.who}</span>
            {h.from === null ? " first said " : ` went from ${SCORE_WORD[h.from] ?? h.from} to `}
            <span className="text-ink">{SCORE_WORD[h.to] ?? h.to}</span> on {h.group}
            {" · "}
            {new Date(h.changedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            {/* ⛔ Marked, so a change the app suggested is never mistaken for
                one somebody volunteered. */}
            {h.source === "suggested" && " · after we asked"}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Where the household actually is (#1331).
 *
 * ⚠️ Only shown when it is WRONG or unset. A settings row nobody needs to touch
 * is a settings row that makes the ones that matter harder to find.
 */
function Timezone({
  household,
  save,
}: {
  household: Household;
  save: { mutate: (b: { timezone: string }) => void; isPending: boolean };
}) {
  const detected = detectedTimezone();
  const agrees = household.timezone === detected;
  if (agrees) return null;

  return (
    <div
      className="rounded-lg border p-3"
      style={{ borderColor: "var(--color-plate-a)" }}
    >
      <p className="text-plate-a text-xs uppercase tracking-wide">Where are you?</p>
      <p className="text-muted mt-1.5 text-xs">
        {household.timezone === "UTC"
          ? "We have not been told, so printed lists use UTC — which can name the wrong day."
          : `Set to ${household.timezone}, but this device says ${detected}.`}
      </p>
      <button
        onClick={() => save.mutate({ timezone: detected })}
        disabled={save.isPending}
        className="text-plate-a mt-2 text-sm underline underline-offset-4 disabled:opacity-50"
      >
        {save.isPending ? "Saving…" : `Use ${detected}`}
      </button>
    </div>
  );
}
